/**
 * The composition root (§1.2 "App composition").
 *
 * One place assembles the whole cross-cutting tree, in an order that is not
 * arbitrary:
 *
 * 1. `PortProvider` — everything below may need a capability.
 * 2. `PreferenceProvider` — needs the key-value port.
 * 3. `EnvironmentProvider` — needs preferences, and publishes the layout
 *    preferences that `@ff/ui`'s ladder resolves against, so it must sit above
 *    anything that measures.
 * 4. `ShortcutProvider` — independent of the three above, but above navigation
 *    so a destination shortcut can be registered by the shell.
 * 5. `NavigationProvider` / `ShellActivityProvider` — shell state proper.
 *
 * A feature packet never repeats any of this. It renders inside the tree and
 * reads what it needs through the typed hooks.
 */

import type { ReactNode } from "react";
import type { ShellActivity } from "./activity";
import { NO_SHELL_ACTIVITY, ShellActivityProvider } from "./activity";
import { EnvironmentProvider } from "./environment";
import { NavigationProvider } from "./navigation";
import type { PortBundle } from "./ports";
import { PortProvider } from "./ports";
import { PreferenceProvider } from "./preferences";
import { ShortcutProvider } from "./shortcuts";
import type { DestinationId } from "../shell/destinations";

export interface AppStateProviderProps {
  readonly ports: PortBundle;
  /**
   * The shell's view of work in progress. Defaults to "nothing in progress",
   * which is the real first-launch state; P15 and P34 supply the live value.
   */
  readonly activity?: ShellActivity;
  /** Where the `data-ff-*` contract is written. Defaults to the document root. */
  readonly rootElement?: HTMLElement | null;
  /** Where key events are listened for. Defaults to the document. */
  readonly shortcutTarget?: EventTarget | null;
  /** Tests only — the app always cold-starts on Train (F-NAV-23). */
  readonly initialDestination?: DestinationId;
  readonly children?: ReactNode;
}

export function AppStateProvider({
  ports,
  activity = NO_SHELL_ACTIVITY,
  rootElement,
  shortcutTarget,
  initialDestination,
  children,
}: AppStateProviderProps): ReactNode {
  return (
    <PortProvider ports={ports}>
      <PreferenceProvider>
        <EnvironmentProvider rootElement={rootElement}>
          <ShortcutProvider target={shortcutTarget}>
            <NavigationProvider initialDestination={initialDestination}>
              <ShellActivityProvider activity={activity}>{children}</ShellActivityProvider>
            </NavigationProvider>
          </ShortcutProvider>
        </EnvironmentProvider>
      </PreferenceProvider>
    </PortProvider>
  );
}
